import clsx from 'clsx';
import type { ForecastContext } from './periods';
import { formatQuarter } from './periods';

const YEAR_COLOR_PALETTE = [
  { bg: 'bg-blue-50', header: 'bg-blue-200', border: 'border-l-blue-400' },
  { bg: 'bg-green-50', header: 'bg-green-200', border: 'border-l-green-400' },
  { bg: 'bg-orange-50', header: 'bg-orange-200', border: 'border-l-orange-400' },
  { bg: 'bg-amber-50', header: 'bg-amber-200', border: 'border-l-amber-400' },
];

function getYearIndex(q: string, fiscalYears: number[]): number {
  const year = parseInt(q.split('_')[1]);
  const idx = fiscalYears.indexOf(year);
  return idx >= 0 ? idx : 0;
}

interface QuarterHeaderRowProps {
  labelWidth?: string;
  ctx: ForecastContext;
}

interface YearHeaderRowProps extends QuarterHeaderRowProps {
  historicalYears?: number[];
}

export function YearHeaderRow({ labelWidth = 'w-64', ctx, historicalYears = [] }: YearHeaderRowProps) {
  const histSet = new Set(historicalYears);
  return (
    <tr>
      <th className={clsx(labelWidth, 'sticky left-0 z-10 bg-white')} />
      {ctx.fiscalYears.map((year, i) => {
        const isHistorical = histSet.has(year);
        return (
          <th
            key={year}
            colSpan={ctx.quartersByYear[year]?.length ?? ctx.periodsPerYear}
            className={clsx(
              'px-3 py-2 text-center text-sm font-bold border-b border-r border-gray-300',
              isHistorical
                ? 'bg-amber-100 text-amber-900'
                : YEAR_COLOR_PALETTE[i % YEAR_COLOR_PALETTE.length].header,
            )}
          >
            FY {year}{isHistorical ? ' (Actual)' : ''}
          </th>
        );
      })}
    </tr>
  );
}

export function QuarterHeaderRow({ labelWidth = 'w-64', ctx }: QuarterHeaderRowProps) {
  return (
    <tr>
      <th
        className={clsx(
          labelWidth,
          'sticky left-0 z-10 bg-gray-50 px-3 py-1.5 text-left text-xs font-medium text-gray-500 border-b border-r border-gray-300',
        )}
      >
        Description
      </th>
      {ctx.allQuarters.map((q) => {
        const idx = getYearIndex(q, ctx.fiscalYears);
        return (
          <th
            key={q}
            className={clsx(
              'px-3 py-1.5 text-right text-xs font-medium text-gray-600 border-b border-r border-gray-200',
              ctx.periodsPerYear === 12
                ? 'min-w-[80px]'
                : ctx.periodsPerYear === 1
                  ? 'min-w-[140px]'
                  : 'min-w-[110px]',
              YEAR_COLOR_PALETTE[idx % YEAR_COLOR_PALETTE.length].bg,
            )}
          >
            {formatQuarter(q)}
          </th>
        );
      })}
    </tr>
  );
}

export function getYearColor(q: string, fiscalYears: number[]): string {
  const idx = getYearIndex(q, fiscalYears);
  return YEAR_COLOR_PALETTE[idx % YEAR_COLOR_PALETTE.length].bg;
}

export function getYearHeaderColor(q: string, fiscalYears: number[]): string {
  const idx = getYearIndex(q, fiscalYears);
  return YEAR_COLOR_PALETTE[idx % YEAR_COLOR_PALETTE.length].header;
}
